"use client"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, X } from "lucide-react"

/**
 * Pick an icon from a curated set, grouped into tabs.
 *
 * Twenty icons you can see at a glance beat every icon in the library behind a
 * search box — the point is to tell things apart at speed, not to find the
 * perfect glyph.
 *
 * The set is a prop, not a hardcoded import: avatar blocks and database tables
 * need different icons but the same picker. Whoever renders it owns the
 * vocabulary.
 *
 * Props:
 *   - value: the stored icon id; empty means nothing was chosen.
 *   - onChange(id): called with the id, or `""` to clear. Empty string and not
 *     null because the API reads null as "leave as is" and "" as "clear it".
 *   - name: the thing's name, only to offer a suggestion.
 *   - groups: `[{ id, label, icons: [{ id, Icon, label }] }]`.
 *   - suggest(name): returns an icon entry to offer, or null.
 *   - neutralIcon: what gets drawn while nothing is chosen.
 *   - emptyHint: the line shown under the grid when nothing is chosen.
 */
export default function IconPicker({
  value,
  onChange,
  name,
  groups,
  suggest,
  neutralIcon: NeutralIcon,
  emptyHint,
}) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(groups?.[0]?.id);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const byId = useMemo(
    () =>
      Object.fromEntries(
        (groups ?? []).flatMap((g) => g.icons).map((i) => [i.id, i])
      ),
    [groups]
  );

  // The button shows what the thing actually shows — a neutral glyph until you pick.
  const Current = (value && byId[value]?.Icon) || NeutralIcon;
  const icons = (groups ?? []).find((g) => g.id === group)?.icons || [];

  // Offered, never applied on its own. The keyword list behind it is a biased
  // guess; it is fine as something you can take or ignore, and was not fine as
  // something drawn on screen as if you had chosen it.
  const suggestion = value ? null : suggest?.(name);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Choose an icon"
        aria-expanded={open}
        title={value ? "Change icon" : "Pick an icon"}
        className={`flex h-10 items-center gap-1.5 rounded-lg border px-2 transition-colors ${
          value
            ? "border-purple-500/50 bg-purple-600/20 text-purple-200"
            : "border-white/20 bg-white/5 text-gray-400 hover:text-white"
        }`}
      >
        {Current && <Current className="h-5 w-5" />}
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute left-0 top-12 z-[10000] w-72 rounded-xl border border-white/15 bg-gray-900 p-3 shadow-2xl">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-white">Icon</p>
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-white"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>

          {suggestion && (
            <button
              type="button"
              onClick={() => {
                onChange(suggestion.id);
                setOpen(false);
              }}
              className="mb-3 flex w-full items-center gap-2 rounded-lg border border-purple-500/30 bg-purple-600/10 px-2.5 py-2 text-left transition-colors hover:bg-purple-600/20"
            >
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-purple-500/20 text-purple-200">
                <suggestion.Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-white">
                  Use “{suggestion.label}”
                </span>
                <span className="block text-[11px] text-gray-400">Suggested from the name</span>
              </span>
            </button>
          )}

          <div className="mb-3 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {(groups ?? []).map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setGroup(g.id)}
                className={`flex-shrink-0 rounded-md px-2 py-1 text-[11px] transition-colors ${
                  group === g.id
                    ? "bg-primary text-white"
                    : "text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-6 gap-1.5">
            {icons.map(({ id, Icon, label }) => {
              const selected = value === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    onChange(id);
                    setOpen(false);
                  }}
                  title={label}
                  aria-label={label}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                    selected
                      ? "border-purple-500 bg-purple-600/30 text-purple-200"
                      : "border-white/10 bg-white/5 text-gray-300 hover:border-purple-400/50 hover:text-white"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {selected && (
                    <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-purple-500 text-white">
                      <Check className="h-2.5 w-2.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {!value && emptyHint && (
            <p className="mt-3 text-[11px] leading-snug text-gray-500">
              {emptyHint}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
